import type { RecommendationsTrackingDataDto } from "../tracking/RecommendationsTrackingDataDto";
import type CubeRecoTracking from "../tracking/cube/CubeRecoTracking";
import type RecoCarousel from "./RecoCarousel";
import type { RecoCarouselContainerDependencies } from "./RecoCarouselContainer";
import { CarouselContainer } from "@breuni-ui/carousel";
import { recoCarouselTagName } from "./recoCarouselTagName";
import { debounceFn } from "../utils/debounceFn";

export const getRecoCarouselContainer = (
  dependencies: RecoCarouselContainerDependencies,
) => {
  return class RecoCarouselContainer extends CarouselContainer {
    private readonly slideIndexKey = "data-slide-index" as const;
    private readonly trackingData:
      | RecommendationsTrackingDataDto
      | null
      | undefined;
    private readonly cubeTracking: CubeRecoTracking;
    private readonly dataVisibleAttributeObserver: MutationObserver;
    private readonly intersectionObserver: IntersectionObserver;
    private readonly startAndStopObservingSelf: () => void;
    private readonly alreadySeenRecoCombinations: Set<string>;
    private readonly abortController: AbortController;
    private abTestIsTracked: boolean;

    constructor() {
      super();
      this.alreadySeenRecoCombinations = new Set();
      this.cubeTracking = new dependencies.CubeRecoTracking();
      this.abortController = new AbortController();
      this.abTestIsTracked = false;
      this.trackingData = this.extractCarouselTrackingDataFromDOM();
      this.intersectionObserver = new dependencies.window.IntersectionObserver(
        this.intersectionObserverCallback.bind(this),
        { threshold: [0.9] },
      );
      this.startAndStopObservingSelf = debounceFn(() => {
        this.intersectionObserver.unobserve(this);
        this.intersectionObserver.observe(this);
      }, 500);
      this.dataVisibleAttributeObserver = new MutationObserver(
        this.mutationObserverCallback.bind(this),
      );
      dependencies.window.addEventListener(
        "pageshow",
        this.onPageShow.bind(this),
        {
          signal: this.abortController.signal,
        },
      );
    }

    connectedCallback(): void {
      super.connectedCallback();

      Array.from(
        this.querySelectorAll<HTMLAnchorElement>("article > a"),
      ).forEach((a) => {
        a.addEventListener("click", (event) => {
          this.handleRecommendationClick(event);
        });
      });

      const carousel = this.querySelector<RecoCarousel>(recoCarouselTagName);
      if (carousel) {
        this.dataVisibleAttributeObserver.observe(carousel, {
          attributes: true,
          subtree: true,
          attributeFilter: ["data-visible"],
        });
      }
    }

    disconnectedCallback(): void {
      super.disconnectedCallback();
      this.dataVisibleAttributeObserver.disconnect();
      this.intersectionObserver.disconnect();
      this.abortController.abort();
    }

    extractCarouselTrackingDataFromDOM(): RecommendationsTrackingDataDto | null {
      const trackingDataRaw = this.querySelector(
        '[data-tracking-data="tracking-data"]',
      )?.textContent;
      return dependencies.textContentToJSON<RecommendationsTrackingDataDto>(
        trackingDataRaw,
      );
    }

    getVisibleIndices(): Array<string> {
      return [
        ...this.querySelectorAll<HTMLElement>('article[data-visible="true"]'),
      ]
        .map((produktEl) => produktEl.getAttribute(this.slideIndexKey))
        .filter((pos): pos is string => pos !== null);
    }

    private isElement(node: Node): node is HTMLElement {
      return node instanceof HTMLElement;
    }

    private onPageShow(event: PageTransitionEvent): void {
      if (event.persisted) {
        this.abTestIsTracked = false;
        this.alreadySeenRecoCombinations.clear();
        this.startAndStopObservingSelf();
      }
    }

    private mutationObserverCallback(mutations: MutationRecord[]): void {
      mutations.forEach((mutation: MutationRecord) => {
        if (
          this.isElement(mutation.target) &&
          mutation.target.hasAttribute("data-is-slide")
        ) {
          this.startAndStopObservingSelf();
        }
      });
    }

    private intersectionObserverCallback(entries: IntersectionObserverEntry[]) {
      entries.forEach((e) => {
        if (e.isIntersecting && this.trackingData) {
          const visibleIndices = this.getVisibleIndices();
          const key = visibleIndices.join("|");
          if (!this.alreadySeenRecoCombinations.has(key)) {
            this.alreadySeenRecoCombinations.add(key);
            this.cubeTracking.trackRecoImpression(
              this.trackingData,
              visibleIndices,
            );
            if (!this.abTestIsTracked) {
              this.cubeTracking.trackABTest(this.trackingData.abTestVariante);
              this.abTestIsTracked = true;
            }
          }
        }
      });
    }

    private handleRecommendationClick(event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();

      const target = event.currentTarget as HTMLElement;
      const clickTrackingUrlData = target.querySelector(
        '[data-click-tracking-url="click-tracking-url"]',
      )?.textContent;

      if (clickTrackingUrlData && this.trackingData?.land) {
        dependencies.AlgonomyTracking.trackClickOnRecommendation(
          clickTrackingUrlData,
          this.trackingData.land,
        );
      }

      const index = target.parentElement?.getAttribute(this.slideIndexKey);
      const href = target.getAttribute("href");

      if (this.trackingData) {
        if (index) {
          this.cubeTracking.trackRecoClick(
            this.trackingData,
            Number.parseInt(index, 10),
          );
        }
      }
      if (href) {
        dependencies.window.open(href, "_self");
      }
    }
  };
};
